// components/ManagedAccountSection.jsx
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { FaTrash, FaPlus, FaInfoCircle, FaLock, FaUserShield } from 'react-icons/fa';
import nonManagedAccountsService from '../../../../service/api/nonManagedAccountsService';
import useSelectedDomain from '../../../../service/Hooks/useSelectedDomain';
import { toast } from 'sonner';
import { FaFile, FaEye } from 'react-icons/fa';

const ManagedAccountSection = ({
  control,
  register,
  errors,
  watch,
  setValue,
  domainId,
  hasError,
  getErrorMessage,
  isUpdateMode, // AJOUT: Pour savoir si on est en mode édition
  existingRepresentatives // AJOUT: Pour afficher les représentants existants
}) => {
  const [nonManagedAccounts, setNonManagedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [representatives, setRepresentatives] = useState([]);
  const selectedDomain = useSelectedDomain();

  // Charger les comptes non administrés
  useEffect(() => {
    const currentDomainId = selectedDomain?.id || domainId;
    if (currentDomainId) {
      fetchNonManagedAccounts(currentDomainId);
    }
  }, [selectedDomain?.id, domainId, searchTerm]);

  // CORRECTION: Si en mode édition, charger les représentants existants
  useEffect(() => {
    if (isUpdateMode && existingRepresentatives && existingRepresentatives.length > 0) {
      // Transformer les représentants existants pour l'affichage
      const formattedRepresentatives = existingRepresentatives.map(rep => ({
        id: rep.id || Date.now(),
        representativeAccountId: rep.accountId,
        mandateProofFile: rep.mandateProofFile || null,
        mandateType: rep.mandateType || '',
        relationshipType: rep.relationshipType || '',
        isExisting: true // Marquer comme existant
      }));
      setRepresentatives(formattedRepresentatives);
      
      // Mettre à jour les valeurs du formulaire
      setValue('RepresentativeAccountIds', formattedRepresentatives.map(r => r.representativeAccountId));
      setValue('MandateTypes', formattedRepresentatives.map(r => r.mandateType));
      setValue('RelationshipTypes', formattedRepresentatives.map(r => r.relationshipType));
    }
  }, [isUpdateMode, existingRepresentatives, setValue]);

  const fetchNonManagedAccounts = async (currentDomainId) => {
    if (!currentDomainId) return;

    setIsLoadingAccounts(true);
    try {
      const options = await nonManagedAccountsService.getNonManagedAccounts(currentDomainId, {
        search: searchTerm,
        pageSize: 100
      });
      setNonManagedAccounts(options);
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Options pour les types de mandat
  const mandateTypeOptions = [
    { value: 'TUTORSHIP', label: 'Tutelle' },
    { value: 'CURATORSHIP', label: 'Curatelle' },
    { value: 'POWER_OF_ATTORNEY', label: 'Procuration' },
    { value: 'LEGAL_GUARDIAN', label: 'Représentant Légal' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'OTHER', label: 'Autre' }
  ];

  // Options pour les types de relation
  const relationshipTypeOptions = [
    { value: 'PARENT', label: 'Parent' },
    { value: 'GUARDIAN', label: 'Tuteur' },
    { value: 'CURATOR', label: 'Curateur' },
    { value: 'LEGAL_REPRESENTATIVE', label: 'Représentant Légal' },
    { value: 'ATTORNEY', label: 'Mandataire' },
    { value: 'OTHER', label: 'Autre' }
  ];

  // CORRECTION: Ajouter un représentant - désactivé en mode édition
  const addRepresentative = () => {
    if (isUpdateMode) {
      toast.warning("Impossible d'ajouter un nouveau représentant en mode édition");
      return;
    }
    
    setRepresentatives(prev => [...prev, {
      id: Date.now(),
      representativeAccountId: null,
      mandateProofFile: null,
      mandateType: '',
      relationshipType: '',
      isExisting: false
    }]);
  };

  // CORRECTION: Supprimer un représentant - seulement si pas existant
  const removeRepresentative = (index) => {
    const representative = representatives[index];
    
    // Ne pas supprimer les représentants existants en mode édition
    if (isUpdateMode && representative.isExisting) {
      toast.warning("Impossible de supprimer un représentant existant en mode édition");
      return;
    }
    
    setRepresentatives(prev => prev.filter((_, i) => i !== index));
    
    // Mettre à jour les tableaux dans le formulaire
    const currentIds = watch('RepresentativeAccountIds') || [];
    const currentTypes = watch('MandateTypes') || [];
    const currentRelationships = watch('RelationshipTypes') || [];
    
    setValue('RepresentativeAccountIds', currentIds.filter((_, i) => i !== index));
    setValue('MandateTypes', currentTypes.filter((_, i) => i !== index));
    setValue('RelationshipTypes', currentRelationships.filter((_, i) => i !== index));
  };

  // CORRECTION: Mettre à jour un représentant - désactivé pour les existants
  const updateRepresentative = (index, field, value) => {
    const representative = representatives[index];
    
    // Ne pas modifier les représentants existants en mode édition
    if (isUpdateMode && representative.isExisting) {
      toast.warning("Impossible de modifier un représentant existant en mode édition");
      return;
    }
    
    setRepresentatives(prev => 
      prev.map((rep, i) => i === index ? { ...rep, [field]: value } : rep)
    );

    // Mettre à jour les tableaux dans le formulaire
    if (field === 'representativeAccountId') {
      const currentIds = watch('RepresentativeAccountIds') || [];
      const newIds = [...currentIds];
      newIds[index] = value;
      setValue('RepresentativeAccountIds', newIds);
    }
    else if (field === 'mandateType') {
      const currentTypes = watch('MandateTypes') || [];
      const newTypes = [...currentTypes];
      newTypes[index] = value;
      setValue('MandateTypes', newTypes);
    }
    else if (field === 'relationshipType') {
      const currentRelationships = watch('RelationshipTypes') || [];
      const newRelationships = [...currentRelationships];
      newRelationships[index] = value;
      setValue('RelationshipTypes', newRelationships);
    }
  };

  return (
    <div className="card border-primary rounded-2 border">
      <div className="card-header bg-light text-white">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaUserShield className="me-2" />
            Compte Administré - Représentants Légaux
          </h5>
          {isUpdateMode && (
            <span className="badge bg-warning text-dark">
              <FaLock className="me-1" />
              Modification restreinte
            </span>
          )}
        </div>
      </div>
      
      <div className="card-body">
        {/* Information importante */}
        <div className={`alert ${isUpdateMode ? 'alert-warning' : 'alert-info'} mb-4`}>
          <div className="d-flex align-items-start">
            <FaInfoCircle className="me-3 mt-1" size={20} />
            <div>
              <strong>Information :</strong> Un compte administré ne nécessite pas d'email.
              <br/>
              {isUpdateMode ? (
                <>
                  <small className="text-warning">
                    <FaLock className="me-1" />
                    <strong>Mode édition :</strong> Les représentants existants ne peuvent pas être modifiés ou supprimés.
                    Seuls les documents de preuve peuvent être mis à jour.
                  </small>
                </>
              ) : (
                <small>
                  Tous les documents et communications seront envoyés aux représentants légaux.
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Liste des mandataires */}
        {representatives.map((representative, index) => (
          <div key={representative.id} className={`border rounded p-3 mb-3 ${representative.isExisting ? 'border-warning bg-warning bg-opacity-10' : ''}`}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <h6 className="mb-0">
                  {representative.isExisting ? (
                    <>
                      <FaUserShield className="me-2 text-warning" />
                      Représentant #{index + 1} (Existante)
                    </>
                  ) : (
                    `Représentant #${index + 1}`
                  )}
                </h6>
                {representative.isExisting && (
                  <span className="badge bg-warning text-dark ms-2">
                    <FaLock size={10} /> Lecture seule
                  </span>
                )}
              </div>
              
              {(!isUpdateMode || !representative.isExisting) && representatives.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeRepresentative(index)}
                  disabled={isUpdateMode && representative.isExisting}
                >
                  <FaTrash />
                </button>
              )}
            </div>

            <div className="row">
              {/* Sélection du compte mandataire */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Compte Mandataire <span className="text-danger">*</span>
                </label>
                <Select
                  options={nonManagedAccounts}
                  isLoading={isLoadingAccounts}
                  onInputChange={setSearchTerm}
                  value={nonManagedAccounts.find(opt => opt.value === representative.representativeAccountId)}
                  onChange={(selected) => 
                    updateRepresentative(index, 'representativeAccountId', selected?.value || null)
                  }
                  placeholder={!selectedDomain?.id ? "Sélectionnez d'abord un domaine" : "Rechercher un compte par nom ou email..."}
                  noOptionsMessage={() => "Aucun compte trouvé"}
                  isDisabled={!selectedDomain?.id || (isUpdateMode && representative.isExisting)}
                  className={`react-select-container ${
                    hasError(`representativeAccountIds[${index}]`) ? 'is-invalid' : ''
                  }`}
                />
                {hasError(`representativeAccountIds[${index}]`) && (
                  <div className="invalid-feedback d-block">
                    {getErrorMessage(`representativeAccountIds[${index}]`)}
                  </div>
                )}
                {isUpdateMode && representative.isExisting && (
                  <small className="text-muted">
                    <FaLock className="me-1" /> Lecture seule - représentant existant
                  </small>
                )}
              </div>

              {/* Type de mandat */}
              <div className="col-md-3 mb-3">
                <label className="form-label">
                  Type de mandat <span className="text-danger">*</span>
                </label>
                <select
                  className={`account-type-select ${
                    hasError(`MandateTypes[${index}]`) ? 'is-invalid' : ''
                  }`}
                  value={representative.mandateType}
                  onChange={(e) => updateRepresentative(index, 'mandateType', e.target.value)}
                  disabled={isUpdateMode && representative.isExisting}
                >
                  <option value="">Sélectionner...</option>
                  {mandateTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {hasError(`MandateTypes[${index}]`) && (
                  <div className="invalid-feedback">
                    {getErrorMessage(`MandateTypes[${index}]`)}
                  </div>
                )}
              </div>

              {/* Type de relation */}
              <div className="col-md-3 mb-3">
                <label className="form-label">
                  Relation <span className="text-danger">*</span>
                </label>
                <select
                  className={`account-type-select ${
                    hasError(`RelationshipTypes[${index}]`) ? 'is-invalid' : ''
                  }`}
                  value={representative.relationshipType}
                  onChange={(e) => updateRepresentative(index, 'relationshipType', e.target.value)}
                  disabled={isUpdateMode && representative.isExisting}
                >
                  <option value="">Sélectionner...</option>
                  {relationshipTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {hasError(`RelationshipTypes[${index}]`) && (
                  <div className="invalid-feedback">
                    {getErrorMessage(`RelationshipTypes[${index}]`)}
                  </div>
                )}
              </div>
            </div>

            {/* Preuve du mandat */}
            <div className="mb-3">
              <label className="form-label">
                Preuve du mandat <span className="text-danger">*</span>
                {representative.isExisting && (
                  <span className="badge bg-info text-dark ms-2">
                    Modifiable
                  </span>
                )}
              </label>
              
              {/* Afficher le fichier existant si présent */}
              {representative.mandateProofFile && (
                <div className="mb-2 p-2 bg-light rounded border">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <FaFile className="me-2 text-primary" />
                      Fichier existant : {representative.mandateProofFile.name || 'document.pdf'}
                    </span>
                    <a 
                      href={URL.createObjectURL(representative.mandateProofFile)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <FaEye size={12} />
                    </a>
                  </div>
                </div>
              )}
              
              <input
                type="file"
                className={`form-control ${
                  hasError(`MandateProofFiles[${index}]`) ? 'is-invalid' : ''
                }`}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  updateRepresentative(index, 'mandateProofFile', file);
                  
                  const currentFiles = watch('MandateProofFiles') || [];
                  const newFiles = [...currentFiles];
                  newFiles[index] = file;
                  setValue('MandateProofFiles', newFiles);
                }}
                disabled={isUpdateMode && representative.isExisting && representative.mandateProofFile}
              />
              {hasError(`MandateProofFiles[${index}]`) && (
                <div className="invalid-feedback">
                  {getErrorMessage(`MandateProofFiles[${index}]`)}
                </div>
              )}
              <div className="form-text">
                Formats acceptés : PDF, JPG, PNG, DOC (max 10MB)
                {isUpdateMode && representative.isExisting && (
                  <span className="text-warning ms-2">
                    <FaLock className="me-1" /> Le document peut être remplacé
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Bouton pour ajouter un mandataire - désactivé en mode édition */}
        {!isUpdateMode && (
          <div className="text-center">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addRepresentative}
            >
              <FaPlus className="me-2" />
              Ajouter un Mandataire
            </button>
          </div>
        )}

        {/* Message si aucun mandataire */}
        {representatives.length === 0 && (
          <div className="alert alert-warning text-center mt-3">
            <strong>Aucun mandataire ajouté.</strong> Veuillez ajouter au moins un représentant légal.
          </div>
        )}

        {/* Message spécial pour mode édition */}
        {isUpdateMode && representatives.length > 0 && (
          <div className="alert alert-info mt-3">
            <div className="d-flex align-items-start">
              <FaLock className="me-3 mt-1" />
              <div>
                <small>
                  <strong>Note :</strong> En mode édition, les représentants légaux existants ne peuvent pas être modifiés ou supprimés.
                  <br/>
                  Seuls les documents de preuve peuvent être mis à jour.
                  <br/>
                  Pour modifier la liste des représentants, veuillez contacter l'administration.
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagedAccountSection;